import { createContext, useContext } from "react";
import type { DetailViewSize } from "#app/components/detail-view/detail-view-popup";
import type { IssueRow } from "#app/lib/data";
import type { IssueViewTab } from "./issue-view-types";
import type { Omittable } from "@salestrak/ui";

// Both members relax: every open call builds this inline from optional
// caller options (`options.workflowId`, `options.address`), and nothing reads
// an absent key differently from an explicit `undefined`. `Omittable` is
// homomorphic, so each member keeps its `mode` literal and the union still
// discriminates.
export type ModalState =
  | Omittable<{ mode: "view"; issue: IssueRow; initialTab: IssueViewTab; focusNoteId?: string | null; focusUnread?: boolean; focusCustomFieldId?: string | null; focusTitleNonce?: number; presentation?: "fullscreen-dialog" }>
  | Omittable<{ mode: "create"; workflowId?: string; stageId?: string; address?: Omittable<{ wholeAddress?: string; latLng?: { lat: number; lng: number } }>; name?: string; issueSourceId?: string; teamId?: string; source?: string }>;

export interface IssueViewDialogState {
  state: ModalState | null;
  setState: (next: ModalState | null) => void;
  activeTab: IssueViewTab;
  setActiveTab: (tab: IssueViewTab) => void;
  desktopSize: DetailViewSize;
  setDesktopSize: (size: DetailViewSize) => void;
  onCreated: (issue: IssueRow) => void;
  closeIssue: () => void;
}

export const IssueViewDialogStateContext = createContext<IssueViewDialogState | null>(null);

export function useIssueViewDialogState(): IssueViewDialogState {
  const ctx = useContext(IssueViewDialogStateContext);
  if (!ctx) throw new Error("useIssueViewDialogState must be within IssueViewProvider");
  return ctx;
}

/** For hosts that may render outside the workspace layout — a null read means
 *  no issue overlay exists to coordinate with. */
export function useIssueViewDialogStateOptional(): IssueViewDialogState | null {
  return useContext(IssueViewDialogStateContext);
}
